// src/components/RegionHistogram/RegionHistogram.jsx
import React, { useMemo } from 'react';
import './Histogram.css';

const RegionHistogram = ({ historicalData = [], onPointe = false }) => {
  // Get the most recent data point's section averages
  const currentAverages = useMemo(() => {
    if (!historicalData || historicalData.length === 0) {
      return null;
    }
    
    // Get the latest data point
    const latest = historicalData[historicalData.length - 1];
    return latest?.sectionAverages || null;
  }, [historicalData]);

  const currentMax = useMemo(() => {
    if (!historicalData || historicalData.length === 0) {
      return null;
    }
    
    // Get the latest data point
    const latest = historicalData[historicalData.length - 1];
    return latest?.max || null;
  }, [historicalData]);

  // Calculate region data for the histogram bars
  const regionData = useMemo(() => {
    if (!currentAverages) {
      return {
        regions: [],
        maxValue: 1,
        totalAverage: 0
      };
    }

    // Define regions with colors - using the pre-calculated averages
    const regions = [
      { 
        name: 'Platform', 
        key: 'platform',
        color: '#e91e63',
        description: 'Platform sensors',
        average: currentAverages.platform || 0
      },
      { 
        name: 'Metatarsal', 
        key: 'metatarsal',
        color: '#4caf50',
        description: 'Metatarsal area',
        average: currentAverages.metatarsal || 0
      },
      { 
        name: 'Front', 
        key: 'front',
        color: '#9c27b0',
        description: 'Front toe area',
        average: currentAverages.front || 0
      },
      { 
        name: 'Back', 
        key: 'back',
        color: '#2196f3',
        description: 'Back heel area',
        average: currentAverages.back || 0
      },
      { 
        name: 'Left Side', 
        key: 'leftTotal',
        color: '#00bcd4',
        description: 'Left side sensors',
        average: currentAverages.leftTotal || 0
      },
      { 
        name: 'Right Side', 
        key: 'rightTotal',
        color: '#ff9800',
        description: 'Right side sensors',
        average: currentAverages.rightTotal || 0
      },
    ];

    // Find max for scaling
    const maxValue = Math.max(...regions.map(r => r.average), 1);

    // Calculate total average
    const totalAverage = Math.round(
      regions.reduce((sum, r) => sum + r.average, 0) / regions.length
    );

    return {
      regions,
      maxValue,
      totalAverage
    };
  }, [currentAverages]);

  // Combined region balance calculations
  const combinedData = useMemo(() => {
    if (!currentAverages) {
      return {
        frontBack: { front: 50, back: 50 },
        leftRight: { left: 50, right: 50 }
      };
    }

    const frontTotal = currentAverages.front || 0;
    const backTotal = currentAverages.back || 0;
    const leftTotal = currentAverages.leftTotal || 0;
    const rightTotal = currentAverages.rightTotal || 0;

    const fbTotal = frontTotal + backTotal;
    const lrTotal = leftTotal + rightTotal;

    return {
      frontBack: {
        front: fbTotal > 0 ? Math.round((frontTotal / fbTotal) * 100) : 50,
        back: fbTotal > 0 ? Math.round((backTotal / fbTotal) * 100) : 50,
      },
      leftRight: {
        left: lrTotal > 0 ? Math.round((leftTotal / lrTotal) * 100) : 50,
        right: lrTotal > 0 ? Math.round((rightTotal / lrTotal) * 100) : 50,
      }
    };
  }, [currentAverages]);

  // Calculate trend data from historical averages (optional enhancement)
  const trendData = useMemo(() => {
    if (!historicalData || historicalData.length < 2) {
      return null;
    }

    // Compare recent average to older average
    const recentCount = Math.min(10, Math.floor(historicalData.length / 2));
    const recentData = historicalData.slice(-recentCount);
    const olderData = historicalData.slice(-recentCount * 2, -recentCount);

    if (olderData.length === 0) return null;

    const calcAvg = (data, key) => {
      const values = data
        .map(d => d.sectionAverages?.[key])
        .filter(v => v !== undefined && v !== null);
      return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    };

    return {
      platform: calcAvg(recentData, 'platform') - calcAvg(olderData, 'platform'),
      metatarsal: calcAvg(recentData, 'metatarsal') - calcAvg(olderData, 'metatarsal'),
      front: calcAvg(recentData, 'front') - calcAvg(olderData, 'front'),
      back: calcAvg(recentData, 'back') - calcAvg(olderData, 'back'),
    };
  }, [historicalData]);

  const hasData = currentAverages !== null;

  // Helper to get trend indicator
  const getTrendIndicator = (key) => {
    if (!trendData || !trendData[key]) return null;
    const change = trendData[key];
    if (Math.abs(change) < 50) return null; // Ignore small changes
    return change > 0 ? '↑' : '↓';
  };

  return (
    <div className={`region-histogram ${!hasData ? 'no-data' : ''} ${!onPointe ? 'off-pointe' : ''}`}>
      <h4>Pressure by Region</h4>
      
      {/* Region Bars */}
      <div className="histogram-bars">
        {regionData.regions.map((region) => (
          <div key={region.key} className="histogram-bar-container">
            <div className="bar-label">
              <span className="region-name">
                {region.name}
                {getTrendIndicator(region.key) && (
                  <span className={`trend-indicator ${trendData[region.key] > 0 ? 'up' : 'down'}`}>
                    {getTrendIndicator(region.key)}
                  </span>
                )}
              </span>
              <span className="region-value">{Math.round(region.average)}</span>
            </div>
            <div className="bar-track">
              <div 
                className="bar-fill"
                style={{
                  width: `${(region.average / regionData.maxValue) * 100}%`,
                  backgroundColor: region.color
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Balance Indicators */}
      <div className="balance-section">
        <h5>Weight Distribution</h5>
        
        {/* Front/Back Balance */}
        <div className="balance-bar">
          <span className="balance-label">Front</span>
          <div className="balance-track">
            <div 
              className="balance-fill front"
              style={{ width: `${combinedData.frontBack.front}%` }}
            />
            <div 
              className="balance-fill back"
              style={{ width: `${combinedData.frontBack.back}%` }}
            />
            <div className="balance-center-line" />
          </div>
          <span className="balance-label">Back</span>
        </div>
        <div className="balance-values">
          <span>{combinedData.frontBack.front}%</span>
          <span>{combinedData.frontBack.back}%</span>
        </div>

        {/* Left/Right Balance */}
        <div className="balance-bar">
          <span className="balance-label">Left</span>
          <div className="balance-track">
            <div 
              className="balance-fill left"
              style={{ width: `${combinedData.leftRight.left}%` }}
            />
            <div 
              className="balance-fill right"
              style={{ width: `${combinedData.leftRight.right}%` }}
            />
            <div className="balance-center-line" />
          </div>
          <span className="balance-label">Right</span>
        </div>
        <div className="balance-values">
          <span>{combinedData.leftRight.left}%</span>
          <span>{combinedData.leftRight.right}%</span>
        </div>
      </div>

      {/* Summary */}
      <div className="histogram-summary">
        <div className="summary-item">
          <span className="summary-value">{regionData.totalAverage}</span>
          <span className="summary-label">Avg Pressure</span>
        </div>
        <div className="summary-item">
          <span className="summary-value">{currentMax}</span>
          <span className="summary-label">Max Pressure</span>
        </div>
        <div className="summary-item">
          <span className="summary-value">{historicalData.length}</span>
          <span className="summary-label">Data Points</span>
        </div>
      </div>

      {/* Off Pointe Overlay */}
      {!onPointe && hasData && (
        <div className="off-pointe-overlay">
          <span>Off Pointe</span>
        </div>
      )}

      {/* No Data Message */}
      {!hasData && (
        <div className="no-data-message">
          <span>No sensor data</span>
        </div>
      )}
    </div>
  );
};

export default RegionHistogram;